
import React from 'react';
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from '@/components/ui/label';
import { Calendar, DollarSign, Users, Baby, Utensils } from 'lucide-react';

interface FestivalDetailsProps {
  place: any;
  isEditing: boolean;
  onInputChange: (field: string, value: any) => void;
}

export function FestivalDetails({ place, isEditing, onInputChange }: FestivalDetailsProps) {
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold flex items-center gap-2">
        <Calendar className="w-5 h-5" />
        Festival Details
      </h3>

      {isEditing ? (
        <>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Start Date</Label>
              <Input
                type="date"
                value={place.start_date || ''}
                onChange={(e) => onInputChange('start_date', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>End Date</Label>
              <Input
                type="date"
                value={place.end_date || ''}
                onChange={(e) => onInputChange('end_date', e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Frequency</Label>
            <Select
              value={place.frequency || ''}
              onValueChange={(value) => onInputChange('frequency', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select frequency" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="one_time">One-time Event</SelectItem>
                <SelectItem value="annual">Annual</SelectItem>
                <SelectItem value="biannual">Biannual</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Ticket Price</Label>
            <Input
              value={place.ticket_price || ''}
              onChange={(e) => onInputChange('ticket_price', e.target.value)}
              placeholder="e.g., $10-50, Free"
            />
          </div>

          <div className="space-y-2">
            <Label>Categories</Label>
            <Input
              value={place.categories?.join(', ') || ''}
              onChange={(e) => onInputChange('categories', e.target.value.split(',').map(s => s.trim()))}
              placeholder="Enter categories separated by commas"
            />
          </div>

          <div className="space-y-2">
            <Label>Capacity</Label>
            <Input
              type="number"
              value={place.capacity || ''}
              onChange={(e) => onInputChange('capacity', e.target.value)}
              placeholder="Maximum number of attendees"
            />
          </div>
        </>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            {place.start_date && (
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-muted-foreground" />
                <span>Starts: {new Date(place.start_date).toLocaleDateString()}</span>
              </div>
            )}
            
            {place.end_date && (
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-muted-foreground" />
                <span>Ends: {new Date(place.end_date).toLocaleDateString()}</span>
              </div>
            )}
          </div>

          {place.frequency && (
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-muted-foreground" />
              <span>Frequency: {place.frequency.replace('_', ' ')}</span>
            </div>
          )}

          {place.ticket_price && (
            <div className="flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-muted-foreground" />
              <span>Tickets: {place.ticket_price}</span>
            </div>
          )}

          {place.categories?.length > 0 && (
            <div className="space-y-2">
              <span className="text-sm text-muted-foreground">Categories:</span>
              <div className="flex flex-wrap gap-2">
                {place.categories.map((category: string) => (
                  <Badge key={category} variant="secondary">
                    {category}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {place.capacity && (
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-muted-foreground" />
              <span>Capacity: {place.capacity}</span>
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            {place.family_friendly && (
              <Badge variant="outline" className="bg-green-50 text-green-700">
                <Baby className="w-4 h-4 mr-1" />
                Family Friendly
              </Badge>
            )}
            
            {place.has_food && (
              <Badge variant="outline">
                <Utensils className="w-4 h-4 mr-1" />
                Food Available
              </Badge>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
